import type {
  Notification,
  NotificationDto,
  NotificationEvent,
  NotificationEventPayload,
} from './notifications.dto';

const decodeBase64ToString = (input: string): string => {
  if (typeof atob === 'function') return atob(input);
  // Node / SSR fallback
  return Buffer.from(input, 'base64').toString('utf-8');
};

/**
 * The notification service marshals `event` as `[]byte`, which the Go json encoder emits as a
 * base64 string. Some swagger generators document this as `array of integer`, and during local
 * mocking it can also arrive already-decoded. All three shapes are handled.
 *
 * Returns null for anything that does not decode. This runs inside the list mapper, so a throw
 * here would reject the whole page and leave the reader with an empty list because of one bad
 * row; instead that row alone becomes an "unavailable" notification.
 */
const decodeEventPayload = (payload: NotificationEventPayload): NotificationEvent | null => {
  try {
    if (Array.isArray(payload)) {
      const bytes = Uint8Array.from(payload);
      return JSON.parse(new TextDecoder().decode(bytes)) as NotificationEvent;
    }
    if (typeof payload === 'string') {
      return JSON.parse(decodeBase64ToString(payload)) as NotificationEvent;
    }
    return payload ?? null;
  } catch (error) {
    console.error("Couldn't decode a notification payload", error);
    return null;
  }
};

/**
 * A frame from the notifications socket: the bare event, JSON-encoded. Null for anything that
 * does not parse, so one bad frame does not break the listener.
 */
export const mapWsNotificationEvent = (frame: unknown): NotificationEvent | null => {
  if (typeof frame !== 'string') return null;
  try {
    const event: unknown = JSON.parse(frame);
    return typeof event === 'object' && event !== null ? (event as NotificationEvent) : null;
  } catch (error) {
    console.error("Couldn't decode a notification frame", error);
    return null;
  }
};

export const mapNotification = (dto: NotificationDto): Notification => ({
  id: dto.id,
  receiver: dto.receiver,
  read: dto.read,
  date: dto.date,
  event: decodeEventPayload(dto.event),
  senderId: dto.sender_id,
  senderRole: dto.sender_role,
});
